import React, { useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, Save, Download, Send, Copy, ExternalLink, Loader2 } from 'lucide-react';

const Field = ({ label, value, onChange, rows = 2, theme, testid }) => (
  <label className={`block text-[11px] font-semibold uppercase tracking-wide ${theme.textSecondary} mb-3`}>
    {label}
    <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows}
      className={`w-full mt-1 px-3 py-2 rounded-lg ${theme.inputBg} border ${theme.cardBorder} ${theme.textPrimary} text-xs font-normal normal-case tracking-normal focus:outline-none focus:border-teal-400/50 resize-y`}
      data-testid={testid} />
  </label>
);

export const ProposalEditor = ({ theme, api, workspaceId, proposal, onClose }) => {
  const { isDarkMode, cardBg, cardBorder, textPrimary, textSecondary, inputBg } = theme;
  const [p, setP] = useState(JSON.parse(JSON.stringify(proposal)));
  const [saving, setSaving] = useState(false);

  const s = p.sections || {};
  const setS = (k, v) => setP(x => ({ ...x, sections: { ...x.sections, [k]: v } }));
  const setInv = (k, v) => setP(x => ({ ...x, investment: { ...x.investment, [k]: v } }));
  const setEmb = (k, v) => setP(x => ({ ...x, embeds: { ...x.embeds, [k]: v } }));
  const setGap = (i, k, v) => setP(x => {
    const gaps = [...(x.gaps || [])];
    gaps[i] = { ...gaps[i], [k]: k === 'est_monthly_loss_usd' ? Number(v) || 0 : v };
    const total = gaps.reduce((t, g) => t + (Number(g.est_monthly_loss_usd) || 0), 0);
    return { ...x, gaps, total_monthly_leak_usd: total };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/workspaces/${workspaceId}/leads-proposals/${p.proposal_id}`, {
        sections: p.sections, investment: p.investment, embeds: p.embeds,
        gaps: p.gaps, total_monthly_leak_usd: p.total_monthly_leak_usd,
      });
      toast.success('Proposal saved');
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    await save();
    try {
      const r = await api.get(`/api/workspaces/${workspaceId}/leads-proposals/${p.proposal_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `${p.business_name}_proposal.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF failed'); }
  };

  const markSent = async () => {
    await save();
    await api.post(`/api/workspaces/${workspaceId}/leads-proposals/${p.proposal_id}/mark-sent`);
    setP(x => ({ ...x, status: 'sent' }));
    toast.success('Marked as sent');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/p/${p.token}`);
    toast.success('Public link copied');
  };

  const sectionCls = `rounded-2xl border ${cardBorder} ${cardBg} p-5`;

  return (
    <div className="mt-6 space-y-4" data-testid="proposal-editor">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <button onClick={onClose}
            className={`p-2 mt-1 rounded-full border ${cardBorder} ${textSecondary} ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
            data-testid="proposal-editor-close">
            <ArrowLeft size={15} />
          </button>
          <div className="min-w-0">
            <h2 className={`text-2xl font-display font-black ${textPrimary} truncate`}>Proposal — {p.business_name}</h2>
            <p className={`text-[11px] ${textSecondary} mt-1`}>Edit everything below, then share the link or download the PDF.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button onClick={copyLink} className={`flex items-center gap-1 text-xs px-3 py-2 rounded-full border ${cardBorder} ${textPrimary} ${inputBg}`}
            data-testid="proposal-editor-copy"><Copy size={12} /> Link</button>
          <a href={`/p/${p.token}`} target="_blank" rel="noreferrer"
            className={`flex items-center gap-1 text-xs px-3 py-2 rounded-full border ${cardBorder} ${textPrimary} ${inputBg}`}
            data-testid="proposal-editor-preview"><ExternalLink size={12} /> Preview</a>
          <button onClick={downloadPdf} className={`flex items-center gap-1 text-xs px-3 py-2 rounded-full border ${cardBorder} ${textPrimary} ${inputBg}`}
            data-testid="proposal-editor-pdf"><Download size={12} /> PDF</button>
          {p.status !== 'sent' && (
            <button onClick={markSent} className="flex items-center gap-1 text-xs px-3 py-2 rounded-full bg-blue-500 text-white font-semibold"
              data-testid="proposal-editor-marksent"><Send size={12} /> Mark sent</button>
          )}
          <button onClick={save} disabled={saving} className="flex items-center gap-1 text-xs px-4 py-2 rounded-full bg-teal-500 text-white font-semibold"
            data-testid="proposal-editor-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
        </div>
      </div>

      <div className={sectionCls}>
        <Field theme={theme} label="Headline" rows={1} value={s.headline} onChange={v => setS('headline', v)} testid="pe-headline" />
        <div className="grid lg:grid-cols-2 gap-x-6">
          <Field theme={theme} label="Intro" rows={4} value={s.intro} onChange={v => setS('intro', v)} testid="pe-intro" />
          <Field theme={theme} label="Leak summary" rows={4} value={s.leak_summary} onChange={v => setS('leak_summary', v)} testid="pe-leak-summary" />
        </div>
      </div>

      <div className={sectionCls} data-testid="pe-gaps-section">
        <p className={`text-base font-semibold ${textPrimary} mb-4`}>Revenue leaks
          <span className="ml-2 text-red-400 text-sm font-bold">${Number(p.total_monthly_leak_usd || 0).toLocaleString()}/mo total</span>
        </p>
        <div className="grid lg:grid-cols-2 gap-3">
          {(p.gaps || []).map((g, i) => (
            <div key={i} className={`rounded-xl border ${cardBorder} ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'} p-3`}>
              <div className="flex gap-2 mb-1.5">
                <input value={g.title || ''} onChange={e => setGap(i, 'title', e.target.value)}
                  className={`flex-1 px-2.5 py-1.5 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs font-semibold focus:outline-none`}
                  data-testid={`pe-gap-title-${i}`} />
                <div className="flex items-center gap-1">
                  <span className="text-red-400 text-xs font-bold">$</span>
                  <input type="number" value={g.est_monthly_loss_usd ?? 0} onChange={e => setGap(i, 'est_monthly_loss_usd', e.target.value)}
                    className={`w-24 px-2 py-1.5 rounded-lg ${inputBg} border ${cardBorder} text-red-400 text-xs font-bold focus:outline-none`}
                    data-testid={`pe-gap-loss-${i}`} />
                  <span className={`text-[10px] ${textSecondary}`}>/mo</span>
                </div>
              </div>
              <textarea value={g.why || ''} onChange={e => setGap(i, 'why', e.target.value)} rows={3}
                className={`w-full px-2.5 py-1.5 rounded-lg ${inputBg} border ${cardBorder} ${textSecondary} text-[11px] focus:outline-none resize-y`}
                data-testid={`pe-gap-why-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <div className={sectionCls}>
        <Field theme={theme} label="Solution intro" value={s.solution_intro} onChange={v => setS('solution_intro', v)} testid="pe-solution-intro" />
        {(s.solutions || []).map((sol, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input value={sol.title || ''} onChange={e => {
              const arr = [...s.solutions]; arr[i] = { ...arr[i], title: e.target.value }; setS('solutions', arr);
            }} className={`w-1/3 px-2.5 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs font-medium focus:outline-none`}
              data-testid={`pe-solution-title-${i}`} />
            <input value={sol.desc || ''} onChange={e => {
              const arr = [...s.solutions]; arr[i] = { ...arr[i], desc: e.target.value }; setS('solutions', arr);
            }} className={`flex-1 px-2.5 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textSecondary} text-xs focus:outline-none`}
              data-testid={`pe-solution-desc-${i}`} />
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className={sectionCls} data-testid="pe-timeline-section">
          <p className={`text-base font-semibold ${textPrimary} mb-3`}>What happens when you deploy</p>
          {(s.deploy_timeline || []).map((t, i) => (
            <div key={i} className="flex gap-2 mb-2 items-center">
              <span className="w-6 h-6 rounded-full bg-teal-400/10 text-teal-400 text-[11px] font-bold flex items-center justify-center shrink-0">{t.step}</span>
              <input value={t.title || ''} onChange={e => {
                const arr = [...s.deploy_timeline]; arr[i] = { ...arr[i], title: e.target.value }; setS('deploy_timeline', arr);
              }} className={`w-1/3 px-2.5 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs font-medium focus:outline-none`}
                data-testid={`pe-timeline-title-${i}`} />
              <input value={t.desc || ''} onChange={e => {
                const arr = [...s.deploy_timeline]; arr[i] = { ...arr[i], desc: e.target.value }; setS('deploy_timeline', arr);
              }} className={`flex-1 px-2.5 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textSecondary} text-xs focus:outline-none`}
                data-testid={`pe-timeline-desc-${i}`} />
            </div>
          ))}
        </div>

        <div className={sectionCls} data-testid="pe-investment-section">
          <p className={`text-base font-semibold ${textPrimary} mb-3`}>Investment</p>
          <div className="grid grid-cols-2 gap-3">
            <label className={`text-[11px] font-semibold uppercase tracking-wide ${textSecondary}`}>Up-front investment
              <input value={p.investment?.upfront || ''} onChange={e => setInv('upfront', e.target.value)} placeholder="e.g. $1,500 setup"
                className={`w-full mt-1 px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs font-normal normal-case tracking-normal focus:outline-none`}
                data-testid="pe-inv-upfront" />
            </label>
            <label className={`text-[11px] font-semibold uppercase tracking-wide ${textSecondary}`}>Monthly retainer
              <input value={p.investment?.retainer || ''} onChange={e => setInv('retainer', e.target.value)} placeholder="e.g. $497/mo"
                className={`w-full mt-1 px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs font-normal normal-case tracking-normal focus:outline-none`}
                data-testid="pe-inv-retainer" />
            </label>
          </div>
          <div className="mt-3">
            <Field theme={theme} label="Note (optional)" rows={1} value={p.investment?.note} onChange={v => setInv('note', v)} testid="pe-inv-note" />
            <Field theme={theme} label="ROI note" value={s.roi_note} onChange={v => setS('roi_note', v)} testid="pe-roi-note" />
          </div>
        </div>
      </div>

      <div className={sectionCls} data-testid="pe-embeds-section">
        <p className={`text-base font-semibold ${textPrimary}`}>Live demo embeds <span className={`text-[10px] font-normal ${textSecondary}`}>(all optional — shown on the public proposal page)</span></p>
        <p className={`text-[11px] ${textSecondary} mb-4`}>Paste the widget embed codes from any of your sub-accounts so the prospect can try the AI right on the proposal.</p>
        <div className="grid lg:grid-cols-2 gap-x-6">
          <Field theme={theme} label="Chat widget embed code" rows={3} value={p.embeds?.chat_code} onChange={v => setEmb('chat_code', v)} testid="pe-embed-chat" />
          <Field theme={theme} label="Orb widget embed code" rows={3} value={p.embeds?.orb_code} onChange={v => setEmb('orb_code', v)} testid="pe-embed-orb" />
        </div>
        <div className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <label className={`text-[11px] font-semibold uppercase tracking-wide ${textSecondary}`}>"Call our AI now" phone number
            <input value={p.embeds?.phone_number || ''} onChange={e => setEmb('phone_number', e.target.value)} placeholder="+1 (555) 123-4567"
              className={`w-full mt-1 px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs font-normal normal-case tracking-normal focus:outline-none`}
              data-testid="pe-embed-phone" />
          </label>
          <label className={`text-[11px] font-semibold uppercase tracking-wide ${textSecondary}`}>Calendar URL (Calendly / GHL)
            <input value={p.embeds?.calendar_url || ''} onChange={e => setEmb('calendar_url', e.target.value)} placeholder="https://calendly.com/…"
              className={`w-full mt-1 px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs font-normal normal-case tracking-normal focus:outline-none`}
              data-testid="pe-embed-calendar" />
          </label>
        </div>
      </div>

      <div className={sectionCls}>
        <Field theme={theme} label="Call to action" rows={1} value={s.cta_text} onChange={v => setS('cta_text', v)} testid="pe-cta" />
      </div>
    </div>
  );
};

export default ProposalEditor;
